import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useStore } from '../store/useStore';
import { db } from '../db/db';
import { BillAllocationModal } from './BillAllocationModal';
import {
  INITIAL_LEDGER_DB, INITIAL_ITEMS_DB, END_OF_LIST,
  VOUCHER_CONFIG, VOUCHER_ORDER, JOURNAL_TYPES, HOTKEY_MAP
} from './voucher/constants';
import { TaxEngine, VoucherEngineLogic } from './voucher/services';
import {
  filterList, fmt, parseFieldId, makeInitialForm,
  parseAmt, calculateNewBalance, updateStockStr
} from './voucher/utils';
import { LedgerCreationView } from '../views/LedgerCreationView';
import { StockItemCreationView } from '../views/StockItemCreationView';
import { VoucherHeader } from './voucher/VoucherHeader';
import { VoucherGrid } from './voucher/VoucherGrid';
import { generateEInvoiceJSON } from '../lib/eInvoiceGenerator';
import { VoucherRightPanel } from './voucher/VoucherRightPanel';

export interface EntryRow {
  drCr: string;
  ledger: { text: string; value: string };
  debit: string;
  credit: string;
}
export interface InventoryRow {
  name: { text: string; value: string };
  qty: string | number;
  rate: string | number;
  disc: string | number;
  amount: string | number;
}
export interface ChargeRow {
  ledger: { text: string; value: string };
  amount: string | number;
}
export interface VoucherData {
  party: { text: string; value: string };
  sales: { text: string; value: string };
  purchase: { text: string; value: string };
  journalRows: EntryRow[];
  items: InventoryRow[];
  charges: ChargeRow[];
  narration: string;
}

function getRowTotals(rows: EntryRow[]) {
  const drTotal = rows.reduce((s, r) => s + (r.drCr === 'Dr' ? (parseAmt(r.debit) || 0) : 0), 0);
  const crTotal = rows.reduce((s, r) => s + (r.drCr === 'Cr' ? (parseAmt(r.credit) || 0) : 0), 0);
  return { drTotal, crTotal, isBalanced: Math.abs(drTotal - crTotal) < 0.005 };
}

export default function VoucherEngine({
  erpLedgers, erpItems, erpVoucherTypes, baseCurrency,
  onSaveVoucher, onQuit, erpVoucherNumber = '', initialDate,
  activeCompanyName, onLedgerCreated, onItemCreated,
  initialVoucherData, isAlterMode, isDuplicateMode, voucherId,
}: any) {
  const [ledgerDb, setLedgerDb] = useState(erpLedgers || INITIAL_LEDGER_DB);
  const [itemDb,   setItemDb]   = useState(erpItems   || INITIAL_ITEMS_DB);
  useEffect(() => { if (erpLedgers) setLedgerDb(erpLedgers); }, [erpLedgers]);
  useEffect(() => { if (erpItems)   setItemDb(erpItems);     }, [erpItems]);

  const derivedVoucherConfig = useMemo(() => {
    const config = { ...VOUCHER_CONFIG };
    erpVoucherTypes?.forEach((vt: any) => {
      if (!config[vt.name]) {
        config[vt.name] = { key: null, useItemGrid: vt.affectsStock, useParty: vt.affectsStock, useJournalGrid: !vt.affectsStock, useTaxSection: vt.affectsStock, defaultDrCr: vt.affectsStock ? [] : ['Dr', 'Cr'] };
      }
    });
    return config;
  }, [erpVoucherTypes]);

  const derivedJournalTypes = useMemo(() => {
    const types = new Set<string>(JOURNAL_TYPES);
    erpVoucherTypes?.forEach((vt: any) => { if (!vt.affectsStock) types.add(vt.name); });
    return types;
  }, [erpVoucherTypes]);

  const LEDGERS          = useMemo(() => Object.keys(ledgerDb).filter(k => ledgerDb[k].group === 'Sundry Debtors'),    [ledgerDb]);
  const CREDITORS        = useMemo(() => Object.keys(ledgerDb).filter(k => ledgerDb[k].group === 'Sundry Creditors'),  [ledgerDb]);
  const SALES_LEDGERS    = useMemo(() => Object.keys(ledgerDb).filter(k => ledgerDb[k].group === 'Sales Accounts'),    [ledgerDb]);
  const PURCHASE_LEDGERS = useMemo(() => Object.keys(ledgerDb).filter(k => ledgerDb[k].group === 'Purchase Accounts'), [ledgerDb]);
  const CASH_BANK        = useMemo(() => Object.keys(ledgerDb).filter(k => ['Cash-in-Hand','Bank Accounts'].includes(ledgerDb[k].group)), [ledgerDb]);
  const EXPENSES         = useMemo(() => Object.keys(ledgerDb).filter(k => ['Indirect Expenses','Direct Expenses','Duties & Taxes'].includes(ledgerDb[k].group)), [ledgerDb]);
  const CHARGE_LEDGERS   = useMemo(() => Object.keys(ledgerDb).filter(k => ['Duties & Taxes','Direct Expenses','Indirect Expenses'].includes(ledgerDb[k].group)), [ledgerDb]);
  const INCOME           = useMemo(() => Object.keys(ledgerDb).filter(k => ledgerDb[k].group === 'Indirect Income'),   [ledgerDb]);
  const ALL_LEDGERS      = useMemo(() => Object.keys(ledgerDb), [ledgerDb]);
  const ITEMS            = useMemo(() => Object.keys(itemDb),   [itemDb]);

  const getLedgerList = (voucherType: string, rowIndex: number) => {
    let list = ALL_LEDGERS;
    if (voucherType === 'Contra')  list = CASH_BANK;
    if (voucherType === 'Payment') list = rowIndex === 0 ? CASH_BANK : [...CREDITORS, ...EXPENSES, ...CASH_BANK];
    if (voucherType === 'Receipt') list = rowIndex === 0 ? [...LEDGERS, ...INCOME, ...CASH_BANK] : CASH_BANK;
    return rowIndex > 0 ? [END_OF_LIST, ...list] : list;
  };

  const globalVoucherType    = useStore(s => s.currentVoucherType);
  const setGlobalVoucherType = useStore(s => s.setCurrentVoucherType);
  const activeCompany        = useStore(s => s.activeCompany);

  const [voucherType,     setVoucherType]     = useState<string>(() => {
    if ((isAlterMode || isDuplicateMode) && initialVoucherData) return initialVoucherData.voucherType;
    return globalVoucherType || 'Sales';
  });
  const [referenceNo, setReferenceNo] = useState<string>(() => {
    if ((isAlterMode || isDuplicateMode) && initialVoucherData) return initialVoucherData.referenceNumber || '';
    return '';
  });
  const [formData,        setFormData]        = useState<VoucherData>(() => {
    if ((isAlterMode || isDuplicateMode) && initialVoucherData) return initialVoucherData.formData;
    return makeInitialForm(globalVoucherType || 'Sales');
  });
  const [voucherDate,     setVoucherDate]     = useState<string>(() => {
    if ((isAlterMode || isDuplicateMode) && initialVoucherData?.date) return initialVoucherData.date;
    return initialDate || '';
  });
  const [focusedField,    setFocusedField]    = useState('party');
  const [selectedIndex,   setSelectedIndex]   = useState(0);
  const [saveError,       setSaveError]       = useState<string | null>(null);
  const [isSaving,        setIsSaving]        = useState(false);
  const [showItemModal,   setShowItemModal]   = useState(false);
  const [showLedgerModal, setShowLedgerModal] = useState(false);
  const [showVoucherTypeModal, setShowVoucherTypeModal] = useState(false);
  const [ledgerModalGroup, setLedgerModalGroup] = useState('Sundry Debtors');
  // Credit limit & stock warnings
  const [creditAlert, setCreditAlert]               = useState<{ outstanding: number; limit: number; party: string } | null>(null);
  const [negativeStockWarn, setNegativeStockWarn]   = useState<string[]>([]);
  // Bill-by-bill allocation modal
  const [billAllocModal, setBillAllocModal] = useState<{ partyName: string; amount: number } | null>(null);
  const [pendingVoucherData, setPendingVoucherData] = useState<any>(null);

  const inputRefs            = useRef<Record<string, any>>({});
  const backspaceArmed       = useRef(false);
  const programmaticFocusRef = useRef(false);
  const prefilledRef         = useRef(false);

  useEffect(() => { if (initialDate) setVoucherDate(initialDate); }, [initialDate]);

  // ── Credit limit check: fires when party changes ──────────────────────────
  useEffect(() => {
    const partyName = formData.party;
    if (!partyName || !activeCompany) { setCreditAlert(null); return; }
    const partyLedger = ledgerDb[partyName] as any;
    if (!partyLedger?.creditLimit || partyLedger.creditLimit <= 0) { setCreditAlert(null); return; }

    db.ledgers.where('[companyId+name]').equals([activeCompany.id!, partyName]).first().then(async (l) => {
      if (!l) return;
      const entries = await db.voucherEntries.where('ledgerId').equals(l.id!).toArray();
      let bal = l.balanceType === 'Dr' ? (l.openingBalance || 0) : -(l.openingBalance || 0);
      entries.forEach(e => { bal += e.type === 'Dr' ? e.amount : -e.amount; });
      const outstanding = Math.abs(bal);
      if (outstanding >= partyLedger.creditLimit * 0.9) {
        setCreditAlert({ outstanding, limit: partyLedger.creditLimit, party: partyName });
      } else {
        setCreditAlert(null);
      }
    });
  }, [formData.party, activeCompany]);

  // ── Negative stock check: fires when items change (sales/CN/DN) ──────────
  useEffect(() => {
    const isOutward = ['Sales', 'Credit Note', 'Debit Note'].includes(voucherType);
    if (!isOutward || !activeCompany || !formData.items?.length) { setNegativeStockWarn([]); return; }

    const check = async () => {
      const warns: string[] = [];
      for (const row of formData.items) {
        if (!row.name?.value || !row.qty) continue;
        const si = await db.stockItems.where('[companyId+name]').equals([activeCompany.id!, row.name.value]).first();
        if (!si) continue;
        const invEntries = await db.inventoryEntries.where('stockItemId').equals(si.id!).toArray();
        let stock = si.openingBalance || 0;
        invEntries.forEach((ie: any) => { stock += ie.quantity || 0; }); // direction handled by sign
        const qty = parseFloat(String(row.qty)) || 0;
        if (qty > stock) warns.push(`${row.name.value} (avail: ${stock.toFixed(2)}, requested: ${qty})`);
      }
      setNegativeStockWarn(warns);
    };
    check();
  }, [formData.items, voucherType, activeCompany]);

  // Pre-fill side-effects (once)
  useEffect(() => {
    if (!initialVoucherData || (!isAlterMode && !isDuplicateMode) || prefilledRef.current) return;
    prefilledRef.current = true;
    
    // Safety net: in case the lazy useState initializer missed it due to strict mode timing issues,
    // explicitly hydrate the state variables from initialVoucherData now.
    const { voucherType: vt, referenceNumber: refNo, date: vDate, formData: initialForm } = initialVoucherData;
    if (vt) {
      setVoucherType(vt);
      setGlobalVoucherType(vt);
    }
    if (refNo !== undefined) setReferenceNo(refNo);
    if (vDate !== undefined) setVoucherDate(vDate);
    if (initialForm) setFormData(initialForm);

    setTimeout(() => {
      focusField('voucherDate');
    }, 50);
  }, [initialVoucherData, isAlterMode, isDuplicateMode, setGlobalVoucherType]);

  // Focus effect
  useEffect(() => {
    if (!isSaving && !showItemModal && !showLedgerModal && programmaticFocusRef.current) {
      const timer = setTimeout(() => {
        const el = inputRefs.current[focusedField];
        if (el) { el.focus(); el.select?.(); }
        programmaticFocusRef.current = false;
      }, 10);
      return () => clearTimeout(timer);
    }
  }, [focusedField, isSaving, showItemModal, showLedgerModal]);

  useEffect(() => { focusField('voucherDate'); }, []);

  useEffect(() => {
    if (isAlterMode || isDuplicateMode) return;
    if (globalVoucherType && globalVoucherType !== voucherType) handleVoucherSwitch(globalVoucherType);
  }, [globalVoucherType, isAlterMode, isDuplicateMode]);

  const firstField = (vt: string) => derivedJournalTypes.has(vt) ? 'journal_0_ledger' : 'party';

  const focusField = (id: string, dataOverride?: any) => {
    const d = dataOverride || formData;
    let target = id;
    const p = parseFieldId(id);
    if (p.type === 'item' && p.col !== 'name' && !d.items[p.index]?.name?.value) target = `item_${p.index}_name`;
    if (p.type === 'journal') {
      if (p.col === 'debit'  && d.journalRows[p.index]?.drCr === 'Cr') target = `journal_${p.index}_credit`;
      if (p.col === 'credit' && d.journalRows[p.index]?.drCr === 'Dr') target = `journal_${p.index}_debit`;
    }
    backspaceArmed.current = false;
    setFocusedField(target);
    setSelectedIndex(0);
    programmaticFocusRef.current = true;
  };

  const handleVoucherSwitch = (newType: string) => {
    if (newType === voucherType) return;
    setGlobalVoucherType(newType);
    const fresh = makeInitialForm(newType);
    setVoucherType(newType); setFormData(fresh); setIsSaving(false);
    setSaveError(null); setReferenceNo('');
    focusField('voucherDate', fresh);
  };

  // Ref field keyboard handler: Enter/Tab → firstField, Shift+Tab/Escape → voucherDate
  const handleRefKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || (e.key === 'Tab' && !e.shiftKey)) {
      e.preventDefault();
      focusField(firstField(voucherType));
    } else if (e.key === 'Escape' || (e.key === 'Tab' && e.shiftKey)) {
      e.preventDefault();
      focusField('voucherDate');
    }
  };

  const currentListContext = useMemo(() => {
    const p = parseFieldId(focusedField);
    if (p.type === 'party') {
      const isBuying = ['Purchase','Debit Note'].includes(voucherType);
      const primaryList = isBuying ? CREDITORS : LEDGERS;
      // If no Sundry Debtors/Creditors exist yet, fall back to ALL_LEDGERS so list isn't blank
      const items = primaryList.length > 0 ? primaryList : ALL_LEDGERS;
      return { title:'List of Ledger Accounts', items, query: formData.party.text, showCreate: true };
    }
    if (p.type === 'sales')    return { title:'List of Ledger Accounts', items: SALES_LEDGERS, query: formData.sales.text, showCreate: true };
    if (p.type === 'purchase') return { title:'List of Ledger Accounts', items: PURCHASE_LEDGERS, query: formData.purchase.text, showCreate: true };
    if (p.type === 'journal' && p.col === 'ledger') return { title:'List of Ledger Accounts', items: derivedJournalTypes.has(voucherType) ? getLedgerList(voucherType, p.index) : ALL_LEDGERS, query: formData.journalRows[p.index]?.ledger.text || '', showCreate: true };
    if (p.type === 'item' && p.col === 'name')     return { title:'List of Stock Items', items: [END_OF_LIST, ...ITEMS], query: formData.items[p.index].name.text, showCreate: true };
    if (p.type === 'charge' && p.col === 'ledger') return { title:'List of Tax & Duties', items: [END_OF_LIST, ...CHARGE_LEDGERS], query: formData.charges[p.index].ledger.text, showCreate: true };
    return { title:'', items:[], query:'', showCreate: false };
  }, [focusedField, formData, voucherType, ledgerDb, itemDb]);

  const currentList = useMemo(() => filterList(currentListContext.items, currentListContext.query), [currentListContext]);

  const getNextField = (id: string, d: any): string => {
    const p = parseFieldId(id);
    const cfg = derivedVoucherConfig[voucherType];
    if (derivedJournalTypes.has(voucherType)) {
      if (p.type === 'journal') {
        if (p.col === 'drcr') return `journal_${p.index}_ledger`;
        if (p.col === 'ledger') {
          if (d.journalRows[p.index].ledger.value === END_OF_LIST) return 'narration';
          return d.journalRows[p.index].drCr === 'Dr' ? `journal_${p.index}_debit` : `journal_${p.index}_credit`;
        }
        if (p.col === 'debit' || p.col === 'credit') {
          const { drTotal, crTotal } = getRowTotals(d.journalRows);
          if (drTotal > 0 && Math.abs(drTotal - crTotal) < 0.005 && p.index === d.journalRows.length - 1) return 'narration';
          return `journal_${p.index + 1}_drcr`;
        }
      }
      return id;
    }
    if (p.type === 'party')    return ['Sales','Credit Note'].includes(voucherType) ? 'sales' : 'purchase';
    if (p.type === 'sales')    return 'item_0_name';
    if (p.type === 'purchase') return 'item_0_name';
    if (p.type === 'item') {
      const { index:i, col:c } = p;
      if (c === 'name') {
        if (!d.items[i].name.text.trim() && i === d.items.length - 1 && (i > 0 && d.items[i-1].name.text.trim() || i === 0)) return cfg.useTaxSection ? 'charge_0_ledger' : 'narration';
        return `item_${i}_qty`;
      }
      if (c === 'qty')    return `item_${i}_rate`;
      if (c === 'rate')   return `item_${i}_disc`;
      if (c === 'disc')   return `item_${i}_amount`;
      if (c === 'amount') return i === d.items.length - 1 ? (cfg.useTaxSection ? 'charge_0_ledger' : 'narration') : `item_${i+1}_name`;
    }
    if (p.type === 'charge') {
      const { index:i, col:c } = p;
      if (c === 'ledger') {
        if (!d.charges[i].ledger.text.trim() && i === d.charges.length - 1 && (i > 0 && d.charges[i-1].ledger.text.trim() || i === 0)) return 'narration';
        return `charge_${i}_amount`;
      }
      if (c === 'amount') return i === d.charges.length - 1 ? 'narration' : `charge_${i+1}_ledger`;
    }
    return id;
  };

  const getPrevField = (id: string, d: any): string => {
    const p = parseFieldId(id);
    const cfg = derivedVoucherConfig[voucherType];
    if (derivedJournalTypes.has(voucherType)) {
      if (p.type === 'narration') { const last = d.journalRows.length - 1; return d.journalRows[last].drCr === 'Dr' ? `journal_${last}_debit` : `journal_${last}_credit`; }
      if (p.type === 'journal') {
        const { index:i, col:c } = p;
        if (c === 'credit' || c === 'debit') return `journal_${i}_ledger`;
        if (c === 'ledger') return `journal_${i}_drcr`;
        if (c === 'drcr' && i > 0) return d.journalRows[i-1].drCr === 'Dr' ? `journal_${i-1}_debit` : `journal_${i-1}_credit`;
        if (c === 'drcr' && i === 0) return 'referenceNo'; // ← first journal row goes back to ref
      }
      return id;
    }
    if (p.type === 'party')    return 'referenceNo'; // ← party goes back to ref
    if (p.type === 'sales')    return 'party';
    if (p.type === 'purchase') return 'party';
    if (p.type === 'item' && p.index === 0 && p.col === 'name') return ['Sales','Credit Note'].includes(voucherType) ? 'sales' : 'purchase';
    if (p.type === 'narration') {
      if (cfg.useTaxSection) { const li = d.charges.length - 1; return d.charges[li].ledger.value ? `charge_${li}_amount` : `charge_${li}_ledger`; }
      const li = d.items.length - 1; return d.items[li].name.value ? `item_${li}_amount` : `item_${li}_name`;
    }
    if (p.type === 'charge') {
      const { index:i, col:c } = p;
      if (c === 'amount') return `charge_${i}_ledger`;
      if (c === 'ledger' && i > 0) return `charge_${i-1}_amount`;
      if (c === 'ledger' && i === 0) { const li = d.items.length - 1; return d.items[li].name.value ? `item_${li}_amount` : `item_${li}_name`; }
    }
    if (p.type === 'item') {
      const { index:i, col:c } = p;
      if (c === 'amount') return `item_${i}_disc`;
      if (c === 'disc')   return `item_${i}_rate`;
      if (c === 'rate')   return `item_${i}_qty`;
      if (c === 'qty')    return `item_${i}_name`;
      if (c === 'name' && i > 0) return `item_${i-1}_amount`;
    }
    return id;
  };

  const processFieldAdvance = (fieldId: string, selectedListValue: string | null = null) => {
    let nd: any = { ...formData };
    const p = parseFieldId(fieldId);
    let autoJumpToNarration = false;

    if (selectedListValue !== null) {
      const isEoL = selectedListValue === END_OF_LIST;
      const accepted = isEoL ? '' : selectedListValue;
      if (p.type === 'party' || p.type === 'sales' || p.type === 'purchase') {
        nd[p.type] = { text: accepted, value: accepted };
        // Auto-fill GST charges when party is selected (Sales/Purchase vouchers)
        if (p.type === 'party' && accepted && derivedVoucherConfig[voucherType]?.useTaxSection) {
          const partyInfo = ledgerDb[accepted];
          const companyInfo = { gstin: activeCompany?.gstin || '', state: activeCompany?.state || '' };
          const autoCharges = TaxEngine.autoFillGst(companyInfo, partyInfo, nd.items, nd.charges, itemDb);
          if (autoCharges) nd.charges = autoCharges;
        }
      } else if (p.type === 'journal') {
        nd.journalRows = [...nd.journalRows];
        nd.journalRows[p.index] = { ...nd.journalRows[p.index], ledger: { text: accepted, value: accepted } };
      } else if (p.type === 'charge') {
        const uc = [...nd.charges]; uc[p.index] = { ...uc[p.index], ledger: { text: accepted, value: accepted } }; nd.charges = uc;
        if (!isEoL) {
          let hasCGST = false, hasSGST = false, hasIGST = false;
          nd.charges.forEach((ch: any) => { if (ch.ledger?.value === 'CGST') hasCGST = true; if (ch.ledger?.value === 'SGST') hasSGST = true; if (ch.ledger?.value === 'IGST') hasIGST = true; });
          if (accepted === 'IGST' && (hasCGST || hasSGST)) { setSaveError('Invalid GST: IGST cannot be used with CGST/SGST.'); nd.charges[p.index] = { ledger: { text:'', value:'' }, amount:'' }; }
          else if ((accepted === 'CGST' || accepted === 'SGST') && hasIGST) { setSaveError('Invalid GST: CGST/SGST cannot be used with IGST.'); nd.charges[p.index] = { ledger: { text:'', value:'' }, amount:'' }; }
        } else nd.charges[p.index].amount = '';
      } else if (p.type === 'item') {
        nd.items = [...nd.items];
        const prevName = nd.items[p.index].name.value;
        const row: any = { ...nd.items[p.index], name: { text: accepted, value: accepted } };
        if (!isEoL) {
          const def = itemDb[accepted];
          if (def) {
            if (prevName !== accepted || !row.rate) row.rate = def.rate;
            const q = parseAmt(row.qty||0), r = parseAmt(row.rate||0);
            if (q > 0 && r > 0 && (prevName !== accepted || !row.amount)) row.amount = TaxEngine.calculateItemAmount(q, r);
            // FEFO: Auto-fill earliest expiring batch for pharmacy
            if ((activeCompany as any)?.businessNature === 'pharmacy' && def.mrp) {
              row.mrp = String(def.mrp || '');
            }
          }
        } else { row.qty = ''; row.rate = ''; row.amount = ''; row.batch = ''; row.expiry = ''; row.mrp = ''; }
        nd.items[p.index] = row;
        // Auto-fill GST when item name changes and party is already selected
        if (!isEoL && nd.party?.value && derivedVoucherConfig[voucherType]?.useTaxSection) {
          const partyInfo   = ledgerDb[nd.party.value];
          const companyInfo = { gstin: activeCompany?.gstin || '', state: activeCompany?.state || '' };
          const autoCharges = TaxEngine.autoFillGst(companyInfo, partyInfo, nd.items, nd.charges, itemDb);
          if (autoCharges) nd.charges = autoCharges;
        }
      }
    }

    if (p.type === 'item' && p.col === 'amount' && p.index === nd.items.length - 1) {
      const last: any = nd.items[nd.items.length - 1];
      if (last.name.value || last.qty || last.rate || last.amount) nd.items = [...nd.items, { name:{text:'',value:''}, qty:'', rate:'', amount:'', batch:'', expiry:'', mrp:'' }];
    }
    if (p.type === 'charge' && p.col === 'amount' && p.index === nd.charges.length - 1) {
      const last: any = nd.charges[nd.charges.length - 1];
      if (last.ledger.value && last.amount) nd.charges = [...nd.charges, { ledger:{text:'',value:''}, amount:'' }];
    }
    if (p.type === 'journal' && (p.col === 'debit' || p.col === 'credit')) {
      const { drTotal, crTotal, isBalanced } = getRowTotals(nd.journalRows);
      if (isBalanced && drTotal > 0 && p.index === nd.journalRows.length - 1) {
        autoJumpToNarration = true;
      } else if (p.index === nd.journalRows.length - 1) {
        const diff = drTotal - crTotal;
        const last: any = nd.journalRows[nd.journalRows.length - 1];
        if (last.ledger.value || last.debit || last.credit) {
          let nextDrCr = 'Dr', prefillAmount = '';
          if (diff > 0) { nextDrCr = 'Cr'; prefillAmount = diff.toFixed(2); }
          else if (diff < 0) { nextDrCr = 'Dr'; prefillAmount = Math.abs(diff).toFixed(2); }
          else nextDrCr = derivedVoucherConfig[voucherType].defaultDrCr[1] || 'Cr';
          nd.journalRows = [...nd.journalRows, { drCr: nextDrCr, ledger:{text:'',value:''}, debit: nextDrCr==='Dr'?prefillAmount:'', credit: nextDrCr==='Cr'?prefillAmount:'' }];
        } else autoJumpToNarration = true;
      }
    }

    if (p.type === 'item' || p.type === 'charge') nd = TaxEngine.recalculateTaxes(nd, ledgerDb, itemDb);
    setFormData(nd);
    focusField(autoJumpToNarration ? 'narration' : getNextField(fieldId, nd), nd);
  };

  const handleInputChange = (fieldId: string, value: string) => {
    backspaceArmed.current = false; setSaveError(null); setSelectedIndex(0);
    setFormData((prev: VoucherData) => {
      let d: any = { ...prev };
      const p = parseFieldId(fieldId);
      if (p.type === 'party' || p.type === 'sales' || p.type === 'purchase') { d[p.type] = { ...d[p.type], text: value }; }
      else if (p.type === 'journal') {
        d.journalRows = [...d.journalRows]; const row: any = { ...d.journalRows[p.index] };
        if (p.col === 'ledger') row.ledger = { ...row.ledger, text: value };
        else if (p.col === 'drcr') { const v = value.toUpperCase(); if (v.startsWith('D')) row.drCr='Dr'; else if (v.startsWith('C')) row.drCr='Cr'; }
        else row[p.col] = value;
        d.journalRows[p.index] = row;
      } else if (p.type === 'narration') { d.narration = value; }
      else if (p.type === 'item') {
        d.items = [...d.items]; const item: any = { ...d.items[p.index] };
        if (p.col === 'name') { item.name = { ...item.name, text: value }; }
        else { item[p.col] = value; if (p.col==='qty'||p.col==='rate'||p.col==='disc') { const q=parseFloat(item.qty||0), r=parseAmt(item.rate||0), discPct=parseFloat(item.disc||0); if (q>0&&r>0) { const gross=q*r; const discAmt=discPct>0?(gross*discPct/100):0; item.amount=(gross-discAmt).toFixed(2); } } }
        d.items[p.index] = item;
      } else if (p.type === 'charge') {
        d.charges = [...d.charges]; const charge: any = { ...d.charges[p.index] };
        if (p.col === 'ledger') charge.ledger = { ...charge.ledger, text: value }; else charge[p.col] = value;
        d.charges[p.index] = charge;
      }
      if (p.type === 'item' || p.type === 'charge') d = TaxEngine.recalculateTaxes(d, ledgerDb);
      // Re-auto-fill GST when item qty/rate/amount changes and party already selected
      if (p.type === 'item' && (p.col === 'qty' || p.col === 'rate' || p.col === 'disc' || p.col === 'amount') && d.party?.value && derivedVoucherConfig[voucherType]?.useTaxSection) {
        const partyInfo   = ledgerDb[d.party.value];
        const companyInfo = { gstin: activeCompany?.gstin || '', state: activeCompany?.state || '' };
        const autoCharges = TaxEngine.autoFillGst(companyInfo, partyInfo, d.items, d.charges, itemDb);
        if (autoCharges) d.charges = autoCharges;
      }
      return d;
    });
  };

  const handleKeyDown = (e: any, fieldId: string) => {
    if (isSaving || showItemModal || showLedgerModal) { e.preventDefault(); return; }
    const p = parseFieldId(fieldId);
    if (e.key === 'ArrowDown') {
      e.preventDefault(); backspaceArmed.current=false;
      if (selectedIndex === -1) { if (currentList.length>0) setSelectedIndex(0); }
      else if (currentList.length>0) setSelectedIndex(prev=>Math.min(prev+1,currentList.length-1));
      return;
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault(); backspaceArmed.current=false;
      if (currentList.length>0) {
        if (selectedIndex === 0 && currentListContext.showCreate) setSelectedIndex(-1); // go to Create
        else if (selectedIndex > 0) setSelectedIndex(prev=>prev-1);
      }
      return;
    }
    // Alt+C → open Create form directly
    if (e.altKey && e.key.toLowerCase() === 'c' && currentListContext.showCreate) {
      e.preventDefault(); e.stopPropagation(); triggerCreateMenu(); return;
    }
    if (e.key === 'Escape')    { e.preventDefault(); backspaceArmed.current=false; const prev=getPrevField(fieldId,formData); if (prev!==fieldId) focusField(prev); return; }
    if (e.key === 'Backspace') {
      const el = inputRefs.current[fieldId];
      if ((el && el.value==='') || p.col==='drcr') {
        e.preventDefault();
        if (backspaceArmed.current || p.col==='drcr') { const prev=getPrevField(fieldId,formData); if (prev!==fieldId) focusField(prev); }
        else backspaceArmed.current = true;
        return;
      }
      backspaceArmed.current = false;
    }
    if (e.key === 'Enter' || e.key === 'Tab') {
      e.preventDefault(); backspaceArmed.current = false;
      if (e.shiftKey) { const prev=getPrevField(fieldId,formData); if (prev!==fieldId) focusField(prev); return; }
      // If Create is highlighted (selectedIndex === -1), open create form
      if (selectedIndex === -1 && currentListContext.showCreate) { triggerCreateMenu(); return; }
      if (fieldId === 'narration') { if (cfg?.useItemGrid) { setFormData((prev: any) => VoucherEngineLogic.autoRoundOff(prev)); } const valRes=VoucherEngineLogic.processVoucher(formData,voucherType); if (valRes.error) { setSaveError(valRes.error); return; } setIsSaving(true); return; }
      const isListField = p.type==='party'||p.type==='sales'||p.type==='purchase'||(p.type==='journal'&&p.col==='ledger')||(p.type==='item'&&p.col==='name')||(p.type==='charge'&&p.col==='ledger');
      if (isListField) {
        const highlighted = currentList[selectedIndex];
        let typedText = '';
        if      (p.type==='item')    typedText = formData.items[p.index].name.text;
        else if (p.type==='charge')  typedText = formData.charges[p.index].ledger.text;
        else if (p.type==='journal') typedText = formData.journalRows[p.index]?.ledger.text||'';
        else                         typedText = (formData as any)[p.type].text;
        processFieldAdvance(fieldId, highlighted||typedText.trim());
      } else processFieldAdvance(fieldId, null);
    }
  };

  const handleListClick = (item: string) => { setSelectedIndex(currentList.indexOf(item)); processFieldAdvance(focusedField, item); };

  const triggerCreateMenu = () => {
    const p = parseFieldId(focusedField);
    if (p.type==='item') { setShowItemModal(true); }
    else if (p.type==='party') { setLedgerModalGroup(['Purchase','Debit Note'].includes(voucherType)?'Sundry Creditors':'Sundry Debtors'); setShowLedgerModal(true); }
    else if (p.type==='sales') { setLedgerModalGroup('Sales Accounts'); setShowLedgerModal(true); }
    else if (p.type==='purchase') { setLedgerModalGroup('Purchase Accounts'); setShowLedgerModal(true); }
    else if (p.type==='charge') { setLedgerModalGroup('Duties & Taxes'); setShowLedgerModal(true); }
    else if (p.type==='journal') { let g='Indirect Expenses'; if(voucherType==='Contra')g='Bank Accounts'; if(voucherType==='Payment'&&p.index===0)g='Bank Accounts'; if(voucherType==='Receipt'&&p.index!==0)g='Bank Accounts'; setLedgerModalGroup(g); setShowLedgerModal(true); }
  };

  const handleItemCreatedLocal = (newItem: any) => {
    setItemDb((prev: any) => ({...prev,[newItem.name]:{rate:newItem.rate,stock:newItem.stock}}));
    onItemCreated?.(newItem); setShowItemModal(false);
    let nd: any = {...formData}; const p=parseFieldId(focusedField);
    nd.items=[...nd.items]; const row: any={...nd.items[p.index],name:{text:newItem.name,value:newItem.name}};
    row.rate=newItem.rate; const q=parseAmt(row.qty||0); if(q>0) row.amount=TaxEngine.calculateItemAmount(q,newItem.rate);
    nd.items[p.index]=row; nd=TaxEngine.recalculateTaxes(nd,ledgerDb);
    setFormData(nd); focusField(getNextField(focusedField,nd),nd);
  };

  const handleLedgerCreatedLocal = (newLedger: any) => {
    setLedgerDb((prev: any) => ({...prev,[newLedger.name]:{group:newLedger.group,balance:newLedger.balance}}));
    onLedgerCreated?.(newLedger); setShowLedgerModal(false);
    const nd: any={...formData}; const p=parseFieldId(focusedField);
    if(p.type==='party'||p.type==='sales'||p.type==='purchase') nd[p.type]={text:newLedger.name,value:newLedger.name};
    else if(p.type==='journal'){nd.journalRows=[...nd.journalRows];nd.journalRows[p.index]={...nd.journalRows[p.index],ledger:{text:newLedger.name,value:newLedger.name}};}
    else if(p.type==='charge'){nd.charges=[...nd.charges];nd.charges[p.index]={...nd.charges[p.index],ledger:{text:newLedger.name,value:newLedger.name}};}
    setFormData(nd); focusField(getNextField(focusedField,nd),nd);
  };

  const closeModals = () => { setShowItemModal(false); setShowLedgerModal(false); setShowVoucherTypeModal(false); programmaticFocusRef.current=true; };

  const handleContainerKeyDown = (e: any) => {
    if (isSaving && !showItemModal && !showLedgerModal && !billAllocModal) {
      if (e.key?.toLowerCase()==='y'||e.key==='Enter') {
        e.preventDefault(); e.stopPropagation();
        const finalReferenceNo=referenceNo.trim() || '';
        const result=VoucherEngineLogic.processVoucher(formData,voucherType);
        if(result.error){setSaveError(result.error);setIsSaving(false);return;}
        if(onSaveVoucher){
          // ── Bill-by-bill check for Receipt / Payment ──────────────────────
          const isBillType = ['Receipt', 'Payment'].includes(voucherType);
          if (isBillType) {
            // Find the Debtor/Creditor journal row
            const partyRow = formData.journalRows?.find((r: any) => {
              if (!r.ledger?.value) return false;
              const info = ledgerDb[r.ledger.value];
              return info && ['Sundry Debtors', 'Sundry Creditors'].includes(info.group)
                     && info.billByBill === 'Yes';
            });
            if (partyRow) {
              const partyAmt = parseAmt(partyRow.debit || partyRow.credit || partyRow.amount || '0');
              if (partyAmt > 0) {
                // Stash voucher data and show modal
                setPendingVoucherData({ voucherType, referenceNumber: finalReferenceNo, date: voucherDate, narration: formData.narration, entries: result.entries, items: formData.items, charges: formData.charges });
                setBillAllocModal({ partyName: partyRow.ledger.value, amount: partyAmt });
                return;
              }
            }
          }
          // ── Normal save (no bill allocation needed) ───────────────────────
          onSaveVoucher({voucherType,referenceNumber:finalReferenceNo,date:voucherDate,narration:formData.narration,entries:result.entries,items:formData.items,charges:formData.charges})
            .then(()=>{setIsSaving(false);setSaveError(null);if(isAlterMode||isDuplicateMode){onQuit?.();}else{const fresh=makeInitialForm(voucherType);setFormData(fresh);setReferenceNo('');focusField('voucherDate',fresh);}})
            .catch((err:any)=>{setSaveError(err.message||'Failed to save.');setIsSaving(false);});
          return;
        }
        setLedgerDb((prev:any)=>{const n={...prev};result.entries.forEach((e:any)=>{if(n[e.ledger])n[e.ledger].balance=calculateNewBalance(n[e.ledger].balance,e.amount,e.type);});return n;});
        if(derivedVoucherConfig[voucherType].useItemGrid&&formData.items){
          let m=0;if(['Sales','Debit Note'].includes(voucherType))m=-1;if(['Purchase','Credit Note'].includes(voucherType))m=1;
          if(m!==0)setItemDb((prev:any)=>{const n={...prev};formData.items.forEach((it:any)=>{if(it.name.value&&it.qty&&n[it.name.value])n[it.name.value].stock=updateStockStr(n[it.name.value].stock,parseAmt(it.qty)*m);});return n;});
        }
        const fresh=makeInitialForm(voucherType);setFormData(fresh);setIsSaving(false);setSaveError(null);setReferenceNo('');focusField('voucherDate',fresh);
      } else if (e.key?.toLowerCase()==='n'||e.key==='Escape') {
        e.preventDefault(); e.stopPropagation(); programmaticFocusRef.current=true; setFocusedField('narration'); setIsSaving(false);
      }
      return;
    }
    // Ctrl+A = Save voucher (global shortcut)
    if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'a' && !isSaving && !showItemModal && !showLedgerModal) {
      e.preventDefault(); e.stopPropagation();
      if (derivedVoucherConfig[voucherType]?.useItemGrid) { setFormData((prev: any) => VoucherEngineLogic.autoRoundOff(prev)); }
      const valRes = VoucherEngineLogic.processVoucher(formData, voucherType);
      if (valRes.error) { setSaveError(valRes.error); return; }
      setIsSaving(true);
      return;
    }
    if(!isSaving&&!showItemModal&&!showLedgerModal&&!showVoucherTypeModal){
      if(e.key==='F2'){
        e.preventDefault();e.stopPropagation();
        focusField('voucherDate');
        // If already on date field, force select-all so user can type new date directly
        setTimeout(() => {
          const dateInputs = document.querySelectorAll('input[placeholder="D-M-YYYY"]');
          if (dateInputs.length > 0) (dateInputs[0] as HTMLInputElement).select();
        }, 20);
        return;
      }
      if(e.key==='F10'){e.preventDefault();e.stopPropagation();if(!isAlterMode)setShowVoucherTypeModal(true);return;}
      // Alt+R — Repeat Last Voucher (loads last saved voucher of this type as template)
      if(e.altKey && (e.key==='r'||e.key==='R') && !isAlterMode){
        e.preventDefault(); e.stopPropagation();
        if (activeCompany) {
          db.vouchers
            .where('companyId').equals(activeCompany.id!)
            .filter(v => v.type === voucherType && !v.deleted)
            .last()
            .then(async (lastV) => {
              if (!lastV) { useStore.getState().setGlobalMessage('No previous ' + voucherType + ' found to repeat.'); return; }
              useStore.getState().setCurrentAlterVoucherId(lastV.id!);
              useStore.getState().setCurrentIsDuplicateMode(true);
              useStore.getState().setGlobalMessage('Repeating last ' + voucherType + ' — modify as needed.');
            });
        }
        return;
      }
      if(!isAlterMode){
        if(e.altKey&&e.key==='F6'){e.preventDefault();e.stopPropagation();setGlobalVoucherType('Credit Note');return;}
        if(e.altKey&&e.key==='F5'){e.preventDefault();e.stopPropagation();setGlobalVoucherType('Debit Note');return;}
        if(!e.altKey&&!e.ctrlKey&&HOTKEY_MAP[e.key]){e.preventDefault();e.stopPropagation();setGlobalVoucherType(HOTKEY_MAP[e.key]);}
      }
    }
  };

  const { drTotal, crTotal, isBalanced } = getRowTotals(formData.journalRows);

  const canGenerateEInvoice = isAlterMode && voucherId && ['Sales', 'Credit Note', 'Debit Note'].includes(voucherType);

  const handleEInvoice = async () => {
    if (!voucherId) return;
    try {
      const json = await generateEInvoiceJSON(voucherId);
      if (!json) { useStore.getState().setGlobalMessage('Could not generate e-Invoice data'); return; }
      const blob = new Blob([JSON.stringify(json, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `e-Invoice_${voucherType}_${erpVoucherNumber || voucherId}.json`;
      a.click();
      URL.revokeObjectURL(url);
      useStore.getState().setGlobalMessage(`e-Invoice JSON downloaded for ${voucherType} #${erpVoucherNumber}`);
    } catch (e: any) {
      useStore.getState().setGlobalMessage(`e-Invoice error: ${e.message}`);
    }
  };

  return (
    <div className="h-full w-full flex flex-col font-sans select-none overflow-hidden bg-[#eef6ea] text-black relative"
      data-voucher-engine tabIndex={-1} onKeyDown={handleContainerKeyDown}
      ref={(el:any)=>{ if(el&&!programmaticFocusRef.current&&Object.keys(inputRefs.current).length===0)el.focus(); }}
    >
      {showItemModal   && <StockItemCreationView onClose={closeModals} onSave={handleItemCreatedLocal} />}
      {showLedgerModal && <LedgerCreationView initialGroupId={ledgerModalGroup} onClose={closeModals} onSave={handleLedgerCreatedLocal} />}

      {isSaving && (
        <div className="absolute inset-0 bg-black/10 flex items-center justify-center z-50">
          <div className="bg-[#d9e3eb] border-[3px] border-[#1e8585] w-[260px] shadow-2xl flex flex-col items-center py-5">
            <div className="text-[13px] text-black mb-1 font-semibold">{isAlterMode?'Alter':'Create'} {voucherType} Voucher</div>
            <div className="text-[11px] text-[#1e8585] mb-1 font-medium">No. {erpVoucherNumber||(isAlterMode?'—':'Auto')}</div>
            {referenceNo && <div className="text-[11px] text-gray-600 mb-1">Ref: {referenceNo}</div>}
            <div className="text-[11px] text-gray-600 mb-3">Date: {voucherDate?((p=voucherDate.split('-'))=>p.length===3?`${parseInt(p[2])}-${parseInt(p[1])}-${p[0]}`:voucherDate)():('—')}</div>
            <div className="text-[16px] font-bold mb-4">Accept ?</div>
            <div className="flex space-x-6 text-[13px] font-semibold">
              <span className="cursor-pointer hover:text-[#1e8585]"><u>Y</u>es</span>
              <span className="cursor-pointer hover:text-[#1e8585]"><u>N</u>o</span>
            </div>
          </div>
        </div>
      )}

      {/* e-Invoice button (floating, only for applicable vouchers) */}
      {canGenerateEInvoice && (
        <div className="absolute top-1 right-12 z-20">
          <button onClick={handleEInvoice} className="text-[10px] font-bold px-2 py-[2px] bg-[#1e8585] text-white rounded hover:bg-[#166e6e] transition-colors shadow-sm" title="Download e-Invoice JSON (NIC format)">
            e-Invoice ↓
          </button>
        </div>
      )}

      <div className="flex flex-1 min-h-0 overflow-hidden w-full">
        <div className="w-[70%] flex flex-col relative bg-[#eef6ea] h-full min-h-0 overflow-hidden">
          <div className="flex flex-col shrink-0 flex-none z-10 w-full bg-[#eef6ea]">
            <VoucherHeader
              voucherType={voucherType} isAlterMode={isAlterMode}
              derivedVoucherConfig={derivedVoucherConfig} referenceNo={referenceNo}
              setReferenceNo={setReferenceNo} erpVoucherNumber={erpVoucherNumber}
              voucherDate={voucherDate} setVoucherDate={setVoucherDate}
              focusedField={focusedField} focusField={focusField} firstField={firstField}
              handleVoucherSwitch={handleVoucherSwitch} formData={formData}
              ledgerDb={ledgerDb} handleInputChange={handleInputChange}
              handleKeyDown={handleKeyDown} inputRefs={inputRefs}
              handleRefKeyDown={handleRefKeyDown}
            />
            {/* ── Credit Limit Warning Banner ─────────────────────────── */}
            {creditAlert && (
              <div className="mx-1 mb-1 px-2 py-1 bg-amber-50 border border-amber-400 rounded flex items-center gap-2 text-[11px]">
                <span className="text-amber-600 font-bold text-[13px]">⚠</span>
                <span className="font-semibold text-amber-800">{creditAlert.party}</span>
                <span className="text-amber-700">
                  Outstanding ₹{creditAlert.outstanding.toLocaleString('en-IN', {maximumFractionDigits:0})}
                  {creditAlert.outstanding >= creditAlert.limit
                    ? <span className="text-red-700 font-bold"> — CREDIT LIMIT EXCEEDED (Limit: ₹{creditAlert.limit.toLocaleString('en-IN')})</span>
                    : <span className="text-amber-600"> nearing credit limit of ₹{creditAlert.limit.toLocaleString('en-IN')}</span>
                  }
                </span>
                <button onClick={() => setCreditAlert(null)} className="ml-auto text-amber-400 hover:text-amber-700 leading-none">✕</button>
              </div>
            )}
            {/* ── Negative Stock Warning Banner ───────────────────────── */}
            {negativeStockWarn.length > 0 && (
              <div className="mx-1 mb-1 px-2 py-1 bg-red-50 border border-red-300 rounded text-[11px]">
                <span className="font-bold text-red-700">⚠ Low/insufficient stock: </span>
                <span className="text-red-600">{negativeStockWarn.join(' • ')}</span>
              </div>
            )}
          </div>
          <VoucherGrid
            voucherType={voucherType} derivedVoucherConfig={derivedVoucherConfig}
            derivedJournalTypes={derivedJournalTypes} formData={formData}
            focusedField={focusedField} focusField={focusField}
            handleInputChange={handleInputChange} handleKeyDown={handleKeyDown}
            inputRefs={inputRefs} ledgerDb={ledgerDb} baseCurrency={baseCurrency}
            drTotal={drTotal} crTotal={crTotal}
          />
          <div className="flex flex-col shrink-0 flex-none border-t border-[#aebec8] bg-[#eef6ea] z-10 w-full">
            <div className="p-1 flex items-start bg-[#eef6ea]">
              <div className="text-[13px] mr-2 w-[70px] pt-[2px] font-semibold">Narration:</div>
              <textarea
                ref={(el:any)=>{inputRefs.current['narration']=el;}}
                value={formData.narration}
                onChange={e=>handleInputChange('narration',e.target.value)}
                onFocus={()=>focusField('narration')}
                onKeyDown={e=>handleKeyDown(e,'narration')}
                rows={2}
                className={`flex-1 min-w-0 outline-none resize-none text-[13px] py-1 px-1 border box-border max-w-full ${focusedField==='narration'?'bg-[#eefcfc] border-transparent':'bg-transparent border-transparent'}`}
                spellCheck={false}
              />
            </div>
            {/* Amount in Words */}
            {(() => {
              const total = drTotal || crTotal || 0;
              if (total <= 0) return null;
              const toW = (x: number): string => {
                if (x === 0) return '';
                const ones = ['','One','Two','Three','Four','Five','Six','Seven','Eight','Nine','Ten','Eleven','Twelve','Thirteen','Fourteen','Fifteen','Sixteen','Seventeen','Eighteen','Nineteen'];
                const tens = ['','','Twenty','Thirty','Forty','Fifty','Sixty','Seventy','Eighty','Ninety'];
                if (x < 20) return ones[x] + ' ';
                if (x < 100) return tens[Math.floor(x/10)] + (x%10 ? ' ' + ones[x%10] : '') + ' ';
                if (x < 1000) return ones[Math.floor(x/100)] + ' Hundred ' + toW(x%100);
                if (x < 100000) return toW(Math.floor(x/1000)) + 'Thousand ' + toW(x%1000);
                if (x < 10000000) return toW(Math.floor(x/100000)) + 'Lakh ' + toW(x%100000);
                return toW(Math.floor(x/10000000)) + 'Crore ' + toW(x%10000000);
              };
              const words = 'Rupees ' + toW(Math.floor(total)).trim() + ' Only';
              return (
                <div className="px-2 py-1 bg-[#f0f4e8] border-t border-[#d4dcc4] text-[11px]">
                  <span className="text-gray-500 font-semibold mr-1">Amount:</span>
                  <span className="text-gray-700 font-bold italic">{words}</span>
                </div>
              );
            })()}
            {/* Save Button */}
            <div className="flex items-center gap-2 px-2 py-1 bg-[#e2ebe2] border-t border-[#c8d4c8]">
              <button
                type="button"
                onClick={() => {
                  if (derivedVoucherConfig[voucherType]?.useItemGrid) { setFormData((prev: any) => VoucherEngineLogic.autoRoundOff(prev)); }
                  const valRes = VoucherEngineLogic.processVoucher(formData, voucherType);
                  if (valRes.error) { setSaveError(valRes.error); return; }
                  setIsSaving(true);
                }}
                className="flex items-center gap-1.5 bg-[#1e8585] text-white text-[12px] font-bold px-4 py-[5px] rounded hover:bg-[#166e6e] transition-colors shadow-sm"
              >
                ✓ Save (Ctrl+A)
              </button>
              <span className="text-[10px] text-gray-500 italic">Enter on Narration also saves</span>
            </div>
          </div>
        </div>

        <VoucherRightPanel
          currentListContext={currentListContext} currentList={currentList}
          selectedIndex={selectedIndex} voucherType={voucherType}
          derivedJournalTypes={derivedJournalTypes} ledgerDb={ledgerDb} itemDb={itemDb}
          focusedField={focusedField} baseCurrency={baseCurrency}
          drTotal={drTotal} crTotal={crTotal} isBalanced={isBalanced}
          saveError={saveError} handleListClick={handleListClick}
          triggerCreateMenu={triggerCreateMenu}
        />
      </div>

      {showVoucherTypeModal && (
        <div className="absolute inset-0 z-50 bg-black/30 flex items-center justify-center"
          onKeyDown={(e:any)=>{if(e.key==='Escape'){e.stopPropagation();setShowVoucherTypeModal(false);}}}
          tabIndex={0} ref={(el:any)=>el&&el.focus()}>
          <div className="bg-white border hover:border-[#1e8585] shadow-[0_4px_20px_rgba(0,0,0,0.3)] w-[300px] flex flex-col pointer-events-auto">
            <div className="bg-[#1e8585] text-white px-3 py-[6px] font-bold text-[14px]">Select Voucher Type</div>
            <div className="max-h-[400px] overflow-y-auto py-1">
              {erpVoucherTypes?.map((vt:any)=>(
                <div key={vt.name} className="px-4 py-2 hover:bg-[#1e8585] hover:text-white cursor-pointer text-[13px] text-black border-b border-gray-100 last:border-0"
                  onClick={()=>{setGlobalVoucherType(vt.name);setShowVoucherTypeModal(false);}}>{vt.name}</div>
              ))}
              {(!erpVoucherTypes||erpVoucherTypes.length===0)&&<div className="px-4 py-3 text-[13px] text-gray-500 italic">No custom voucher types found.</div>}
            </div>
            <div className="text-center py-2 text-[11px] text-gray-500 bg-[#f9f9f9] border-t">Press ESC to cancel</div>
          </div>
        </div>
      )}

      {/* ── Bill-by-Bill Allocation Modal ───────────────────────────────── */}
      {billAllocModal && pendingVoucherData && activeCompany && (
        <BillAllocationModal
          partyName={billAllocModal.partyName}
          totalAmount={billAllocModal.amount}
          voucherType={pendingVoucherData.voucherType}
          companyId={activeCompany.id!}
          onAccept={(allocs) => {
            setBillAllocModal(null);
            const dataWithAllocs = { ...pendingVoucherData, billAllocations: allocs };
            setPendingVoucherData(null);
            if (onSaveVoucher) {
              onSaveVoucher(dataWithAllocs)
                .then(() => {
                  setIsSaving(false); setSaveError(null);
                  if (isAlterMode || isDuplicateMode) { onQuit?.(); }
                  else { const fresh = makeInitialForm(voucherType); setFormData(fresh); setReferenceNo(''); focusField('voucherDate', fresh); }
                })
                .catch((err: any) => { setSaveError(err.message || 'Failed to save.'); setIsSaving(false); });
            }
          }}
          onSkip={() => {
            setBillAllocModal(null);
            const data = pendingVoucherData;
            setPendingVoucherData(null);
            if (onSaveVoucher) {
              onSaveVoucher(data)
                .then(() => {
                  setIsSaving(false); setSaveError(null);
                  if (isAlterMode || isDuplicateMode) { onQuit?.(); }
                  else { const fresh = makeInitialForm(voucherType); setFormData(fresh); setReferenceNo(''); focusField('voucherDate', fresh); }
                })
                .catch((err: any) => { setSaveError(err.message || 'Failed to save.'); setIsSaving(false); });
            }
          }}
        />
      )}
    </div>
  );
}
